import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, Modal } from '@/components/kit';
import apiClient from '@/services/apiClient';

/**
 * THE EXPORT, TAKEN — not described.
 *
 * A clinic that cannot walk out with its own records is a clinic that was
 * never really asked to stay, so this is the one button on the privacy screen
 * that must do exactly what its label says.
 *
 * It states the counts first, because "export everything" is only trustworthy
 * if you can see what everything is, and because a manager who sees 0 visits
 * has learnt something before they wait for a zip.
 */

interface Summary {
  patients: number;
  visits: number;
  appointments: number;
  bills: number;
  prescriptions: number;
  accessLog: number;
}

const unwrap = <T,>(body: unknown): T => ((body as { data?: T })?.data ?? body) as T;
const n = (v: number) => v.toLocaleString('en-US');

const LINES: Array<[keyof Summary, string]> = [
  ['patients', 'Patients'],
  ['visits', 'Visits'],
  ['appointments', 'Appointments'],
  ['bills', 'Bills'],
  ['prescriptions', 'Prescriptions'],
  ['accessLog', 'Access log'],
];

export function ExportEverything({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [taking, setTaking] = useState(false);
  const [failed, setFailed] = useState(false);

  const { data: summary } = useQuery({
    queryKey: ['cm-export-summary'],
    enabled: open,
    staleTime: 60_000,
    retry: false,
    queryFn: async (): Promise<Summary | null> => {
      const res = await apiClient.get('/data-export/summary');
      return unwrap<Summary | null>(res.data);
    },
  });

  /* The zip comes back on the same authenticated client every other read uses,
     so it arrives as a blob and is handed to the browser here. A plain <a href>
     would be an unauthenticated request and would 401. */
  const take = async () => {
    setTaking(true); setFailed(false);
    try {
      const res = await apiClient.get('/data-export/everything.zip', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `maie-export-${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      onClose();
    } catch {
      setFailed(true);
    } finally {
      setTaking(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Export everything"
      sub="this clinic's records, as CSV in one zip"
      width={460}
      foot={
        <>
          <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={taking} onClick={() => void take()}>
            {taking ? 'Preparing…' : 'Download'}
          </button>
        </>
      }
    >
      <div style={{ marginTop: 12 }}>
        <Card>
          <p className="t-ovl mut">WHAT LEAVES</p>
          {summary
            ? LINES.map(([key, label]) => (
                <p key={key} className="t-sec sec" style={{ marginTop: 4 }}>
                  {label} · <span className="w6">{n(summary[key])}</span>
                </p>
              ))
            : <p className="t-sec mut" style={{ marginTop: 4 }}>Counting…</p>}
        </Card>
        <p className="t-cap mut mt8">
          One CSV per kind, plus a manifest. Taking it is logged.
        </p>
        {failed ? (
          <p className="t-sec red mt8">That did not download. Nothing was changed — try again.</p>
        ) : null}
      </div>
    </Modal>
  );
}
